import { useCallback, useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { BadgeCheck, CalendarCheck, Check, Minus, Plus, Send, Share2, Sparkles } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { API_URL } from "@/data/content";
import { useAuth } from "@/context/AuthContext";
import WishShareModal from "@/components/WishShareModal";
import Parallax from "@/components/Parallax";
import { SectionHead } from "@/components/Reveal";

const ROLES = ["Old Boy (Dosco)", "Parent", "Visitor", "Well-wisher"];
const RSVP_ROLES = ["Old Boy (Dosco)", "Parent", "Visitor", "Well-wisher", "Staff"];
const HOUSES = ["—", "Hyderabad", "Jaipur", "Kashmir", "Oberoi", "Tata", "Staff"];
const DAY_LABELS = [["1", "Day 1 · Oct 22"], ["2", "Day 2 · Oct 23"], ["3", "Day 3 · Oct 24"]];

function WishCard({ w, onShare, withTestId = true }) {
  const tid = (id) => (withTestId ? { "data-testid": id } : {});
  return (
    <div {...tid(`wish-card-${w.id}`)} className="glass mx-3 flex w-[320px] shrink-0 flex-col rounded-2xl px-5 py-4 sm:w-[360px]">
      <p className="font-serif2 text-[15px] italic leading-relaxed text-fg/90">"{w.message}"</p>
      <div className="mt-3 flex items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-1.5">
          <span className="truncate text-xs font-semibold tracking-wide text-fg">{w.name}</span>
          {w.verified && (
            <span title="Signed-in member" className="flex items-center">
              <BadgeCheck className="h-3.5 w-3.5 shrink-0 text-gold" {...tid(`wish-verified-${w.id}`)} />
            </span>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <span className="rounded-full border border-gold/30 px-3 py-1 font-mono text-[9px] uppercase tracking-[0.2em] text-gold">
            {w.house && w.house !== "—" ? `${w.house} · ` : ""}
            {w.role}
          </span>
          <button
            onClick={() => onShare(w)}
            aria-label="Share this wish as a card"
            {...tid(`wish-share-${w.id}`)}
            className="flex h-7 w-7 items-center justify-center rounded-full border border-line/25 text-soft transition-all duration-300 hover:border-gold/50 hover:text-gold"
          >
            <Share2 className="h-3 w-3" />
          </button>
        </div>
      </div>
    </div>
  );
}

function MarqueeRow({ items, reverse, dur, onShare }) {
  const trackRef = useRef(null);

  useEffect(() => {
    const el = trackRef.current;
    if (!el || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;
    let x = 0;
    let paused = false;
    let raf;
    const host = el.parentElement;
    const enter = () => { paused = true; };
    const leave = () => { paused = false; };
    host.addEventListener("mouseenter", enter);
    host.addEventListener("mouseleave", leave);
    const seconds = parseFloat(dur) || 55;
    const loop = () => {
      const w = el.scrollWidth / 2;
      if (w > 0 && !paused) {
        const v = window.__lenis?.velocity || 0;
        const boost = 1 + Math.min(Math.abs(v) * 0.05, 2.6);
        const step = ((w / (seconds * 60)) * boost);
        x += reverse ? step : -step;
        if (reverse && x >= 0) x -= w;
        if (!reverse && x <= -w) x += w;
        el.style.transform = `translateX(${x}px)`;
      }
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      host.removeEventListener("mouseenter", enter);
      host.removeEventListener("mouseleave", leave);
    };
  }, [reverse, dur]);

  const build = (withTestId) => items.map((w) => <WishCard key={w.id} w={w} onShare={onShare} withTestId={withTestId} />);
  return (
    <div className="marquee-hover overflow-hidden py-2">
      <div ref={trackRef} className="flex w-max will-change-transform" style={reverse ? { transform: "translateX(-50%)" } : undefined}>
        <div className="flex">{build(true)}</div>
        <div className="flex" aria-hidden="true">{build(false)}</div>
      </div>
    </div>
  );
}

export default function Guestbook() {
  const { user } = useAuth();
  const [wishes, setWishes] = useState([]);
  const [form, setForm] = useState({ name: "", role: "Old Boy (Dosco)", house: "—", message: "" });
  const [sending, setSending] = useState(false);
  const [inspiring, setInspiring] = useState(false);
  const [shareWish, setShareWish] = useState(null);
  const [rsvp, setRsvp] = useState({ name: "", email: "", role: "Old Boy (Dosco)", days: ["1", "2", "3"], guests: 2 });
  const [rsvpSending, setRsvpSending] = useState(false);
  const [rsvpSummary, setRsvpSummary] = useState(null);
  const [rsvpConfirmed, setRsvpConfirmed] = useState(false);

  const loadWishes = useCallback(() => {
    axios
      .get(`${API_URL}/wishes`)
      .then((r) => setWishes(r.data))
      .catch(() => toast.error("Could not load the wish wall — try refreshing."));
  }, []);

  useEffect(() => {
    loadWishes();
    axios.get(`${API_URL}/rsvps/summary`).then((r) => setRsvpSummary(r.data)).catch(() => {});
  }, [loadWishes]);

  useEffect(() => {
    const onChanged = () => loadWishes();
    window.addEventListener("doon:wishes-changed", onChanged);
    return () => window.removeEventListener("doon:wishes-changed", onChanged);
  }, [loadWishes]);

  useEffect(() => {
    if (user?.name) {
      setForm((f) => ({ ...f, name: f.name || user.name }));
      setRsvp((r) => ({ ...r, name: r.name || user.name, email: r.email || user.email || "" }));
    }
  }, [user]);

  const rsvpSubmit = async (e) => {
    e.preventDefault();
    if (rsvpSending) return;
    if (rsvp.days.length === 0) {
      toast.error("Pick at least one day you're attending");
      return;
    }
    setRsvpSending(true);
    try {
      const { data } = await axios.post(`${API_URL}/rsvps`, {
        name: rsvp.name,
        email: rsvp.email,
        role: rsvp.role,
        days: rsvp.days,
        guests: rsvp.guests,
      });
      setRsvpSummary(data.summary);
      setRsvpConfirmed(true);
      toast.success(`You're on the list, ${data.rsvp.name.split(" ")[0]} — ${data.rsvp.guests} attending!`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save your RSVP — please try again.");
    } finally {
      setRsvpSending(false);
    }
  };

  const inspire = async () => {
    if (inspiring) return;
    setInspiring(true);
    setForm((f) => ({ ...f, message: "" }));
    try {
      const res = await fetch(`${API_URL}/wishes/inspire`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ role: form.role, house: form.house }),
      });
      if (!res.ok) throw new Error("inspire failed");
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const lines = buf.split("\n\n");
        buf = lines.pop();
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const payload = line.slice(6);
          if (payload === "[DONE]") continue;
          const j = JSON.parse(payload);
          if (j.delta) setForm((f) => ({ ...f, message: (f.message + j.delta).slice(0, 240) }));
        }
      }
    } catch (e) {
      toast.error("Could not conjure a wish — please try again");
    } finally {
      setInspiring(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      const { data } = await axios.post(`${API_URL}/wishes`, {
        name: form.name,
        role: form.role,
        house: form.house,
        message: form.message,
      });
      setWishes((prev) => [data, ...prev]);
      setForm({ name: user?.name || "", role: form.role, house: "—", message: "" });
      toast.success("Your wish is on the Chandbagh wall!");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save your wish — please try again.");
    } finally {
      setSending(false);
    }
  };

  const row1 = wishes.filter((_, i) => i % 2 === 0);
  const row2 = wishes.filter((_, i) => i % 2 === 1);

  return (
    <section id="guestbook" className="relative overflow-hidden py-24 lg:py-32">
      <Parallax speed={70}>
        <div className="pointer-events-none absolute left-0 top-1/4 h-[380px] w-[380px] rounded-full bg-gold/10 blur-[130px]" />
      </Parallax>
      <div className="mx-auto max-w-4xl px-6">
        <SectionHead
          eyebrow="RSVP & Digital Guestbook"
          title="Leave a Wish for Chandbagh"
          sub="Doscos, parents and friends — write a short wish for the 91st Founder's Day, paint it into a shareable card, and watch it join the wall of greetings."
        />

        <motion.div
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.15 }}
          transition={{ duration: 0.7 }}
          className="glass mb-8 rounded-3xl border-gold/30 p-6 sm:p-8"
          data-testid="rsvp-card"
        >
          <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
            <div>
              <h3 className="flex items-center gap-2 font-display text-lg font-bold text-fg" data-testid="rsvp-title">
                <CalendarCheck className="h-5 w-5 text-gold" /> Confirm Your Attendance
              </h3>
              <p className="mt-1 text-xs text-soft">91st Founder's Day • October 22–24, 2026 • Chandbagh, Dehradun</p>
            </div>
            <span className="rounded-full border border-gold/40 bg-gold/10 px-4 py-2 font-mono text-[10px] tracking-[0.2em] text-gold" data-testid="rsvp-summary">
              {rsvpSummary ? `${rsvpSummary.headcount} ATTENDING • ${rsvpSummary.rsvps} RSVPS` : "…"}
            </span>
          </div>
          <form onSubmit={rsvpSubmit} className="mt-6 grid gap-4 sm:grid-cols-2" data-testid="rsvp-form">
            <input
              required
              minLength={2}
              maxLength={60}
              value={rsvp.name}
              onChange={(e) => setRsvp({ ...rsvp, name: e.target.value })}
              placeholder="Your name"
              data-testid="rsvp-name-input"
              className="w-full rounded-xl border border-line/20 bg-transparent px-4 py-3 text-sm text-fg outline-none transition-colors placeholder:text-soft/60 focus:border-gold/60"
            />
            <input
              type="email"
              required
              value={rsvp.email}
              onChange={(e) => setRsvp({ ...rsvp, email: e.target.value })}
              placeholder="Your email"
              data-testid="rsvp-email-input"
              className="w-full rounded-xl border border-line/20 bg-transparent px-4 py-3 text-sm text-fg outline-none transition-colors placeholder:text-soft/60 focus:border-gold/60"
            />
            <select
              value={rsvp.role}
              onChange={(e) => setRsvp({ ...rsvp, role: e.target.value })}
              data-testid="rsvp-role-select"
              className="w-full appearance-none rounded-xl border border-line/20 bg-panel px-4 py-3 text-sm text-fg outline-none transition-colors focus:border-gold/60"
            >
              {RSVP_ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
            <div className="flex items-center justify-between rounded-xl border border-line/20 px-2 py-2" data-testid="rsvp-guests-stepper">
              <span className="pl-3 text-xs text-soft">Total headcount (incl. you)</span>
              <div className="flex items-center gap-1">
                <button type="button" onClick={() => setRsvp({ ...rsvp, guests: Math.max(1, rsvp.guests - 1) })} aria-label="Fewer guests" data-testid="rsvp-guests-minus" className="flex h-8 w-8 items-center justify-center rounded-full text-soft transition-colors hover:bg-gold/15 hover:text-gold">
                  <Minus className="h-3.5 w-3.5" />
                </button>
                <span className="w-8 text-center font-display text-sm font-bold text-gold" data-testid="rsvp-guests-count">{rsvp.guests}</span>
                <button type="button" onClick={() => setRsvp({ ...rsvp, guests: Math.min(20, rsvp.guests + 1) })} aria-label="More guests" data-testid="rsvp-guests-plus" className="flex h-8 w-8 items-center justify-center rounded-full text-soft transition-colors hover:bg-gold/15 hover:text-gold">
                  <Plus className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
            <div className="sm:col-span-2">
              <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.3em] text-gold">Which Days?</label>
              <div className="flex flex-wrap gap-2">
                {DAY_LABELS.map(([id, label]) => (
                  <button
                    type="button"
                    key={id}
                    onClick={() => setRsvp((r) => ({ ...r, days: r.days.includes(id) ? r.days.filter((x) => x !== id) : [...r.days, id] }))}
                    data-testid={`rsvp-day-${id}`}
                    className={`rounded-full px-4 py-2 text-xs font-medium tracking-wide transition-all duration-300 ${rsvp.days.includes(id) ? "border border-gold/60 bg-gold/15 text-gold" : "border border-line/20 text-soft hover:text-fg"}`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
            <button
              type="submit"
              disabled={rsvpSending}
              data-testid="rsvp-submit-button"
              className="ripple-host flex items-center justify-center gap-2 rounded-full bg-gold py-3.5 font-display text-sm font-bold tracking-[0.2em] text-navy transition-all duration-300 hover:scale-[1.02] disabled:opacity-60 sm:col-span-2"
            >
              {rsvpSending ? "SENDING…" : <><Check className="h-4 w-4" /> {rsvpConfirmed ? "UPDATE MY RSVP" : "CONFIRM ATTENDANCE"}</>}
            </button>
          </form>
          {rsvpConfirmed && (
            <p className="mt-3 text-xs text-green-300" data-testid="rsvp-confirmed-note">
              You're confirmed — {rsvp.guests} of you attending. Update your RSVP anytime.
            </p>
          )}
        </motion.div>

        <motion.form
          onSubmit={submit}
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.15 }}
          transition={{ duration: 0.7 }}
          className="glass rounded-3xl p-6 sm:p-8"
          data-testid="guestbook-form"
        >
          <div className="grid gap-4 sm:grid-cols-3">
            <div>
              <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.3em] text-gold">Your Name</label>
              <input
                required
                minLength={2}
                maxLength={40}
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. Arjun Sharma"
                data-testid="guestbook-name-input"
                className="w-full rounded-xl border border-line/20 bg-transparent px-4 py-3 text-sm text-fg outline-none transition-colors placeholder:text-soft/60 focus:border-gold/60"
              />
            </div>
            <div>
              <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.3em] text-gold">You Are A</label>
              <select
                value={form.role}
                onChange={(e) => setForm({ ...form, role: e.target.value })}
                data-testid="guestbook-role-select"
                className="w-full appearance-none rounded-xl border border-line/20 bg-panel px-4 py-3 text-sm text-fg outline-none transition-colors focus:border-gold/60"
              >
                {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            <div>
              <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.3em] text-gold">House / Affiliation</label>
              <select
                value={form.house}
                onChange={(e) => setForm({ ...form, house: e.target.value })}
                data-testid="guestbook-house-select"
                className="w-full appearance-none rounded-xl border border-line/20 bg-panel px-4 py-3 text-sm text-fg outline-none transition-colors focus:border-gold/60"
              >
                {HOUSES.map((h) => <option key={h} value={h}>{h}</option>)}
              </select>
            </div>
          </div>
          <div className="mt-4">
            <div className="mb-2 flex items-center justify-between gap-3">
              <label className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold">Your Wish</label>
              <button
                type="button"
                onClick={inspire}
                disabled={inspiring}
                data-testid="inspire-button"
                className="flex items-center gap-1.5 font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-gold transition-opacity hover:opacity-75 disabled:opacity-50"
              >
                <Sparkles className={`h-3 w-3 ${inspiring ? "animate-pulse" : ""}`} />
                {inspiring ? "Writing…" : "Need inspiration?"}
              </button>
            </div>
            <textarea
              required
              minLength={3}
              maxLength={240}
              rows={3}
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              placeholder="A short message for the 91st Founder's Day…"
              data-testid="guestbook-message-input"
              className="w-full resize-none rounded-xl border border-line/20 bg-transparent px-4 py-3 text-sm text-fg outline-none transition-colors placeholder:text-soft/60 focus:border-gold/60"
            />
            {user && (
              <p className="mt-2 font-mono text-[9px] uppercase tracking-[0.2em] text-soft/70" data-testid="guestbook-posting-as">
                Posting as {user.name} — your wish earns the gold tick
              </p>
            )}
          </div>
          <button
            type="submit"
            disabled={sending}
            data-testid="guestbook-submit-button"
            className="ripple-host mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-gold py-4 font-display text-sm font-bold tracking-[0.2em] text-navy transition-all duration-300 hover:scale-[1.02] hover:shadow-[0_0_40px_rgba(212,175,55,0.3)] disabled:opacity-60 sm:w-auto sm:px-10"
          >
            {sending ? "SENDING…" : <><Send className="h-4 w-4" /> ADD TO THE WISH WALL</>}
          </button>
        </motion.form>
      </div>

      <div className="mt-14 space-y-2" data-testid="guestbook-marquee">
        <div className="flex items-center justify-center gap-2 pb-4 font-mono text-[10px] uppercase tracking-[0.35em] text-soft">
          <Sparkles className="h-3 w-3 text-gold" /> The Wish Wall — tap the share icon on any wish
        </div>
        {row1.length > 0 && <MarqueeRow items={row1} dur="55s" onShare={setShareWish} />}
        {row2.length > 0 && <MarqueeRow items={row2} reverse dur="65s" onShare={setShareWish} />}
      </div>

      <WishShareModal wish={shareWish} onClose={() => setShareWish(null)} />
    </section>
  );
}
